/**
 * Hair mask landing page — gallery, benefits slider, FAQs, subscription UI, variant picker, marquee, how-to video, upsell.
 * LP-specific selectors (see page.hair-mask-lp.liquid). The 10-minute-mask.js asset remains the backup for page.10-minute-mask.liquid.
 */
document.addEventListener('DOMContentLoaded', () => {
  /**
   * Scroll a container so `target` aligns to the visible area.
   * Uses bounding rects — `offsetLeft - offsetLeft` mixes offsetParent frames and breaks horizontal carousels.
   */
  const scrollToTarget = (container, target, horizontal = true) => {
    if (horizontal) {
      const left =
        target.getBoundingClientRect().left -
        container.getBoundingClientRect().left +
        container.scrollLeft;
      container.scrollTo({ left, behavior: 'smooth' });
    } else {
      const top =
        target.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop;
      container.scrollTo({ top, behavior: 'smooth' });
    }
  };

  // --- 1. Main product gallery & thumbnails ---
  const mainGalleryContainer = document.querySelector('[data-hair-mask-lp="product-gallery"]');

  if (mainGalleryContainer) {
    const mainImagesList = mainGalleryContainer.querySelectorAll('[data-media-id]');

    document.querySelectorAll('[data-hair-mask-lp-gallery-index]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const idx = btn.getAttribute('data-hair-mask-lp-gallery-index');
        if (idx == null || idx === '') return;
        const slide = mainGalleryContainer.querySelector(`[data-index="${idx}"]`);
        if (slide) {
          scrollToTarget(mainGalleryContainer, slide, true);
        }
      });
    });

    const getClosestSlideIndexToGalleryCenter = () => {
      const slides = Array.from(
        mainGalleryContainer.querySelectorAll('[data-media-id]'),
      );
      if (!slides.length) return 0;
      const rootRect = mainGalleryContainer.getBoundingClientRect();
      const midX = rootRect.left + rootRect.width / 2;
      let bestIdx = 0;
      let bestDist = Infinity;
      slides.forEach((slide, i) => {
        const r = slide.getBoundingClientRect();
        const c = r.left + r.width / 2;
        const dist = Math.abs(c - midX);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      });
      return bestIdx;
    };

    const galleryRoot = mainGalleryContainer.closest('.hair-mask-lp-gallery');
    const galleryNavPrev = galleryRoot?.querySelector('.hair-mask-lp-gallery__cursor-zone--prev');
    const galleryNavNext = galleryRoot?.querySelector('.hair-mask-lp-gallery__cursor-zone--next');

    /** → / ← style arrows as custom cursors (data URLs). Hotspot at center of 48×48. */
    const CURSOR_ARROW_NEXT_SVG =
      '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"><path d="M10 24h22M28 14l10 10-10 10" fill="none" stroke="#000000" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const CURSOR_ARROW_PREV_SVG =
      '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"><path d="M38 24H16M20 14L10 24l10 10" fill="none" stroke="#000000" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    const cursorFromSvg = (svgMarkup) =>
      `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}") 24 24, pointer`;

    const CURSOR_NEXT = cursorFromSvg(CURSOR_ARROW_NEXT_SVG);
    const CURSOR_PREV = cursorFromSvg(CURSOR_ARROW_PREV_SVG);

    const updateGalleryNavEdgeState = () => {
      if (!(galleryNavPrev instanceof HTMLButtonElement) || !(galleryNavNext instanceof HTMLButtonElement)) {
        return;
      }
      const slides = mainGalleryContainer.querySelectorAll('[data-media-id]');
      const n = slides.length;
      if (n <= 1) {
        galleryNavPrev.disabled = true;
        galleryNavNext.disabled = true;
        galleryNavPrev.style.cursor = '';
        galleryNavNext.style.cursor = '';
        return;
      }
      const idx = getClosestSlideIndexToGalleryCenter();
      const atFirst = idx === 0;
      const atLast = idx === n - 1;

      galleryNavPrev.disabled = atFirst;
      galleryNavNext.disabled = atLast;
      galleryNavPrev.style.cursor = atFirst ? '' : CURSOR_PREV;
      galleryNavNext.style.cursor = atLast ? '' : CURSOR_NEXT;
    };

    document.querySelectorAll('[data-hair-mask-lp-gallery-step]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (btn instanceof HTMLButtonElement && btn.disabled) return;
        const raw = btn.getAttribute('data-hair-mask-lp-gallery-step');
        const delta = raw ? parseInt(raw, 10) : 0;
        if (!Number.isFinite(delta) || delta === 0) return;
        const slides = Array.from(
          mainGalleryContainer.querySelectorAll('[data-media-id]'),
        );
        if (!slides.length) return;
        const current = getClosestSlideIndexToGalleryCenter();
        const next = Math.max(0, Math.min(slides.length - 1, current + delta));
        const target = slides[next];
        if (target) {
          scrollToTarget(mainGalleryContainer, target, true);
        }
      });
    });

    const thumbnailsContainer = document.querySelector('[data-hair-mask-lp="product-thumbnails"]');
    let syncThumbnailsToGalleryScroll = () => {};

    if (thumbnailsContainer && mainImagesList.length) {
    const thumbnailsList = thumbnailsContainer.querySelectorAll(
      'button[data-media-id]',
    );
    const thumbScrollContainer = thumbnailsContainer.querySelector('.hair-mask-lp-thumbs__track');

    thumbnailsList.forEach((thumb) => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        thumbnailsList.forEach((t) => t.classList.remove('active'));
        thumb.classList.add('active');

        const mediaId = thumb.getAttribute('data-media-id');
        const targetImage = [...mainImagesList].find(
          (img) => img.getAttribute('data-media-id') === mediaId,
        );

        if (targetImage) {
          scrollToTarget(mainGalleryContainer, targetImage, true);
        }
      });
    });

    /**
     * Pick the slide whose center is closest to the gallery viewport center.
     * Works when swiping forward or backward; keeps vertical thumbs scrolled into view.
     */
    const thumbsStripVisible = () =>
      Boolean(
        thumbnailsContainer &&
          thumbnailsContainer.getBoundingClientRect().height > 0,
      );

    syncThumbnailsToGalleryScroll = () => {
      if (!mainGalleryContainer || !mainImagesList.length) return;

      const rootRect = mainGalleryContainer.getBoundingClientRect();
      const midX = rootRect.left + rootRect.width / 2;
      let bestSlide = null;
      let bestDist = Infinity;

      mainImagesList.forEach((slide) => {
        const r = slide.getBoundingClientRect();
        const center = r.left + r.width / 2;
        const dist = Math.abs(center - midX);
        if (dist < bestDist) {
          bestDist = dist;
          bestSlide = slide;
        }
      });

      if (!bestSlide) return;

      const mediaId = bestSlide.getAttribute('data-media-id');
      let targetThumb = null;

      thumbnailsList.forEach((thumb) => {
        if (thumb.getAttribute('data-media-id') === mediaId) {
          thumb.classList.add('active');
          targetThumb = thumb;
        } else {
          thumb.classList.remove('active');
        }
      });

      if (targetThumb && thumbScrollContainer && thumbsStripVisible()) {
        targetThumb.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    };

    }

    const galleryTracker = mainGalleryContainer.parentElement?.querySelector(
      '[data-hair-mask-lp-gallery-tracker]',
    );
    const galleryTrackerThumb = galleryTracker?.querySelector(
      '.hair-mask-lp-gallery__tracker-thumb',
    );

    const updateGalleryScrollTracker = () => {
      if (
        !(galleryTracker instanceof HTMLElement) ||
        !(galleryTrackerThumb instanceof HTMLElement)
      ) {
        return;
      }
      const slides = mainGalleryContainer.querySelectorAll('[data-media-id]');
      const n = slides.length;
      if (n <= 1) {
        galleryTracker.classList.add('hair-mask-lp-hidden');
        return;
      }
      galleryTracker.classList.remove('hair-mask-lp-hidden');
      const thumbPct = 100 / n;
      galleryTrackerThumb.style.width = `${thumbPct}%`;
      const maxScroll = mainGalleryContainer.scrollWidth - mainGalleryContainer.clientWidth;
      if (maxScroll <= 0) {
        galleryTrackerThumb.style.left = '0%';
        return;
      }
      const t = mainGalleryContainer.scrollLeft / maxScroll;
      galleryTrackerThumb.style.left = `${t * (100 - thumbPct)}%`;
    };

    let galleryScrollRaf = null;
    const onGalleryScroll = () => {
      if (galleryScrollRaf) cancelAnimationFrame(galleryScrollRaf);
      galleryScrollRaf = requestAnimationFrame(() => {
        syncThumbnailsToGalleryScroll();
        updateGalleryScrollTracker();
        updateGalleryNavEdgeState();
      });
    };

    mainGalleryContainer.addEventListener('scroll', onGalleryScroll, { passive: true });

    const onGalleryResize = () => {
      updateGalleryScrollTracker();
      updateGalleryNavEdgeState();
    };

    requestAnimationFrame(() => {
      syncThumbnailsToGalleryScroll();
      updateGalleryScrollTracker();
      updateGalleryNavEdgeState();
    });
    window.addEventListener('resize', onGalleryResize);
  }

  // --- 2. Featured benefits slider (LP: #hair-mask-lp-what-to-expect; legacy ids still supported) ---
  const benefitsSection =
    document.getElementById('hair-mask-lp-what-to-expect') ||
    document.getElementById('what-to-expect') ||
    document.getElementById('benefits-slider');
  if (benefitsSection) {
    const benefitsScrollContainer =
      document.getElementById('benefits-slider-track') ||
      benefitsSection.querySelector('[data-benefits-image] #benefits-slider-track');

    if (benefitsScrollContainer) {
      const slides = benefitsScrollContainer.querySelectorAll('figure');
      const indicatorsContainer = benefitsSection.querySelector('.slide-indicators');
      const indicators = indicatorsContainer
        ? indicatorsContainer.querySelectorAll('button')
        : [];

      const featureButtons = Array.from(
        benefitsSection.querySelectorAll('[data-benefits-content] > button'),
      ).filter((btn) => btn.querySelector('[data-benefits-feature]'));

      const mobileTabButtons = Array.from(
        benefitsSection.querySelectorAll('[data-benefits-mobile-tabs] button'),
      );
      const hasImageCarousel = slides.length > 1;

      const getGapPx = () => {
        const s = getComputedStyle(benefitsScrollContainer);
        const g = s.columnGap && s.columnGap !== 'normal' ? s.columnGap : s.gap;
        const n = parseFloat(g);
        return Number.isFinite(n) ? n : 16;
      };

      const scrollToSlideIndex = (index) => {
        const slide = slides[index];
        if (!slide) return;
        const gapPx = getGapPx();
        let left = 0;
        for (let i = 0; i < index; i++) {
          left += slides[i].offsetWidth + gapPx;
        }
        const maxScroll =
          benefitsScrollContainer.scrollWidth - benefitsScrollContainer.clientWidth;
        left = Math.max(0, Math.min(left, maxScroll));
        benefitsScrollContainer.scrollTo({ left, behavior: 'smooth' });
      };

      const setActiveSlide = (index) => {
        indicators.forEach((btn, i) => {
          const dot = btn.querySelector('.slide-indicator');
          if (!dot) return;
          if (i === index) dot.classList.add('active');
          else dot.classList.remove('active');
        });

        featureButtons.forEach((btn, i) => {
          const featureDiv = btn.querySelector('div[data-benefits-feature]');
          if (i === index) {
            btn.classList.add('active');
            btn.classList.remove('opacity-35');
            featureDiv?.classList.add('active');
          } else {
            btn.classList.remove('active');
            btn.classList.add('opacity-35');
            featureDiv?.classList.remove('active');
          }
        });

        mobileTabButtons.forEach((btn, i) => {
          if (i === index) {
            btn.classList.remove('opacity-35');
            btn.classList.add('active');
            btn.setAttribute('aria-pressed', 'true');
          } else {
            btn.classList.add('opacity-35');
            btn.classList.remove('active');
            btn.setAttribute('aria-pressed', 'false');
          }
        });
      };

      indicators.forEach((btn, index) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          setActiveSlide(index);
          if (hasImageCarousel) scrollToSlideIndex(index);
        });
      });

      featureButtons.forEach((btn, index) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          setActiveSlide(index);
          if (hasImageCarousel) scrollToSlideIndex(index);
        });
      });

      mobileTabButtons.forEach((btn, index) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          setActiveSlide(index);
          if (hasImageCarousel) scrollToSlideIndex(index);
        });
      });

      const syncActiveFromScroll = () => {
        const rect = benefitsScrollContainer.getBoundingClientRect();
        const mid = rect.left + rect.width / 2;
        let bestIndex = 0;
        let bestDist = Infinity;
        slides.forEach((slide, i) => {
          const r = slide.getBoundingClientRect();
          const c = r.left + r.width / 2;
          const dist = Math.abs(c - mid);
          if (dist < bestDist) {
            bestDist = dist;
            bestIndex = i;
          }
        });
        setActiveSlide(bestIndex);
      };

      if (hasImageCarousel) {
        let scrollTick = null;
        benefitsScrollContainer.addEventListener(
          'scroll',
          () => {
            if (scrollTick) cancelAnimationFrame(scrollTick);
            scrollTick = requestAnimationFrame(syncActiveFromScroll);
          },
          { passive: true },
        );
      }

      if (hasImageCarousel && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
                const indexStr = entry.target.getAttribute('data-index');
                if (!indexStr) return;
                const index = parseInt(indexStr, 10);
                setActiveSlide(index);
              }
            });
          },
          {
            root: benefitsScrollContainer,
            threshold: [0.25, 0.5, 0.75],
          },
        );

        slides.forEach((slide) => observer.observe(slide));
      }

      setActiveSlide(0);
      if (hasImageCarousel) requestAnimationFrame(syncActiveFromScroll);
    }
  }

  // --- 3. Prev/next: thumbnail strip (LP) or generic sliders ---
  const prevNextButtons = document.querySelectorAll('[data-scrollbar-button]');

  prevNextButtons.forEach((btn) => {
    btn.removeAttribute('disabled');
    btn.removeAttribute(':disabled');

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const isNext = btn.getAttribute('data-scrollbar-button') === 'next';
      const thumbStripHost = btn.closest('[data-hair-mask-lp="product-thumbnails"]');
      let container;
      let isHorizontal = true;

      if (thumbStripHost) {
        container = thumbStripHost.querySelector('.hair-mask-lp-thumbs__track');
        isHorizontal = Boolean(
          container && container.clientWidth > container.clientHeight,
        );
      } else {
        const sliderSection = btn.closest('[x-data*="initContentSlider"]');
        if (sliderSection) {
          container = sliderSection.querySelector(
            '[x-ref="scrollContainer"], .grid-container, main',
          );
        }
      }

      if (container) {
        if (isHorizontal) {
          const scrollAmount = container.clientWidth * 0.8;
          container.scrollBy({
            left: isNext ? scrollAmount : -scrollAmount,
            behavior: 'smooth',
          });
        } else {
          const scrollAmount = container.clientHeight * 0.8;
          container.scrollBy({
            top: isNext ? scrollAmount : -scrollAmount,
            behavior: 'smooth',
          });
        }
      }
    });
  });

  // --- 4. FAQ accordion (subscription + variant UI handled in second DOMContentLoaded) ---
  const FAQ_CONTAINER = '[data-style-id="faqs-container-pewadB"]';
  const ANIM_DURATION = 320;

  function setIconOpen(details, isOpen) {
    const icon = details.querySelector('[data-animated-icon]');
    if (!icon) return;
    const [span1, span2] = icon.querySelectorAll('span');
    [span1, span2].forEach((s) => {
      if (s) s.style.transition = 'transform 0.3s ease';
    });
    if (isOpen) {
      if (span1) span1.style.transform = 'rotate(0deg)';
      if (span2) span2.style.transform = 'rotate(0deg)';
    } else {
      if (span1) span1.style.transform = 'rotate(-180deg)';
      if (span2) span2.style.transform = 'rotate(-90deg)';
    }
  }

  function closeAccordion(details) {
    const content = details.querySelector('.accordion-content');
    if (!content) {
      details.removeAttribute('open');
      return;
    }
    content.style.maxHeight = `${content.scrollHeight}px`;
    content.style.overflow = 'hidden';
    content.style.transition = `max-height ${ANIM_DURATION}ms ease`;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        content.style.maxHeight = '0px';
      });
    });
    setIconOpen(details, false);
    setTimeout(() => {
      details.removeAttribute('open');
      content.style.maxHeight = '';
    }, ANIM_DURATION);
  }

  function openAccordion(details) {
    details.setAttribute('open', '');
    const content = details.querySelector('.accordion-content');
    if (!content) return;
    content.style.overflow = 'hidden';
    content.style.transition = `max-height ${ANIM_DURATION}ms ease`;
    content.style.maxHeight = '0px';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        content.style.maxHeight = `${content.scrollHeight}px`;
      });
    });
    setIconOpen(details, true);
    setTimeout(() => {
      content.style.maxHeight = '';
      content.style.overflow = '';
    }, ANIM_DURATION);
  }

  document.querySelectorAll(`${FAQ_CONTAINER} details`).forEach((details) => {
    const content = details.querySelector('.accordion-content');
    if (content && !details.open) {
      content.style.maxHeight = '0px';
      content.style.overflow = 'hidden';
    }
    setIconOpen(details, details.open);

    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const faqContainer = details.closest(FAQ_CONTAINER);
      if (!faqContainer) return;

      const isOpen = details.open;

      faqContainer.querySelectorAll('details').forEach((sibling) => {
        if (sibling !== details && sibling.open) {
          closeAccordion(sibling);
        }
      });

      if (isOpen) {
        closeAccordion(details);
      } else {
        openAccordion(details);
      }
    });
  });

  // --- 5. Sticky product bar: fixed until bottom of .hair-mask-lp hits viewport, then dock inside LP ---
  (function initHairMaskLpProductBarDock() {
    const root = document.querySelector('.hair-mask-lp');
    const bar = document.getElementById('hair-mask-lp-product-bar');
    if (!(root instanceof HTMLElement) || !(bar instanceof HTMLElement)) return;

    const dockClass = 'hair-mask-lp-product-bar--docked';

    const updateDock = () => {
      const rootRect = root.getBoundingClientRect();
      const vh = window.innerHeight;
      // `rootRect.bottom <= vh` is true whenever the LP bottom is anywhere above the
      // viewport bottom (e.g. short LP on load) — wrong for docking.
      const lpTallerThanViewport = rootRect.height > vh;

      const rootBottomDoc = rootRect.bottom + window.scrollY;
      const viewportBottomDoc = window.scrollY + vh;
      // Dock only after the user can scroll the LP: viewport bottom has reached/passed LP bottom.
      const dock = lpTallerThanViewport && viewportBottomDoc >= rootBottomDoc - 1;

      bar.classList.toggle(dockClass, dock);
    };

    updateDock();
    window.addEventListener('scroll', updateDock, { passive: true });
    window.addEventListener('resize', updateDock);
  })();
});

document.addEventListener('DOMContentLoaded', () => {
  const form = document.getElementById('hair-mask-lp-product-form');
  const variantPicker = document.getElementById('hair-mask-lp-variant-picker');
  const variantsData = document.getElementById('hair-mask-lp-variants-data');
  const atcButton = document.getElementById('vanilla-atc-button');
  const subToggle = document.getElementById('vanilla-sub-toggle');
  const variantInput = form?.querySelector('input[name="id"]');
  const sellingPlanInput = form?.querySelector('input[name="selling_plan"]');
  const barAtcButton = document.getElementById('bar-atc-button');
  const barPickerButton = document.getElementById('bar-scroll-to-picker-button');
  const barProductImage = document.getElementById('bar-product-image');
  const barProductTitle = document.getElementById('bar-product-title');
  const barProductOption = document.getElementById('bar-product-option');
  const barProductPlan = document.getElementById('bar-product-plan');
  const barProductCompareAt = document.getElementById('bar-product-compare-at');
  const barProductPrice = document.getElementById('bar-product-price');
  const barProductDiscounted = document.getElementById('bar-product-discounted');
  const alpineData = form && form._x_dataStack ? form._x_dataStack[0] : null;

  if (form && variantPicker && atcButton && variantInput && sellingPlanInput) {
    const options = Array.from(variantPicker.querySelectorAll('.hair-mask-lp-bundle__option'));
    const variants = variantsData?.textContent
      ? JSON.parse(variantsData.textContent)
      : [];
    const variantsById = new Map(variants.map((variant) => [String(variant.id), variant]));

    /** Selling plan per bundle (subscribe); IDs from product URLs — not driven by frequency UI. */
    const sellingPlanForSubscribe = (variant) => {
      if (!variant) return null;
      const raw = variant.subscription_selling_plan_id;
      if (raw == null || raw === '') return null;
      const id = Number(raw);
      if (!Number.isFinite(id)) return null;
      const plans = Array.isArray(variant.selling_plans) ? variant.selling_plans : [];
      const match = plans.find((p) => Number(p?.id) === id);
      return match || { id, name: 'Subscription' };
    };

    const getActiveVariant = () => {
      const activeOption = variantPicker.querySelector('.hair-mask-lp-bundle__option--selected');
      const variantId = activeOption?.getAttribute('data-variant-id');
      if (!variantId) return null;
      return variantsById.get(variantId) || null;
    };

    const formatMoney = (cents) => {
      const amount = Number(cents || 0) / 100;
      return `$${amount.toFixed(2)}`;
    };

    /**
     * Subscribe % off MSRP (compare-at) by jar count.
     * 1 → 20%, 2 → 35%, 3 → 45%.
     */
    const SUBSCRIBE_OFF_COMPARE_BY_JARS = {
      1: 20,
      2: 35,
      3: 45,
    };

    /** MSRP for a variant: compare-at when present, otherwise one-time price. */
    const msrpCents = (compareCents, baseCents) => {
      const c = Number(compareCents || 0);
      const b = Number(baseCents || 0);
      return c > 0 ? c : b;
    };

    const subscribeOffPercentForJars = (jarCount) => {
      const jars = Math.max(1, Number(jarCount) || 1);
      return SUBSCRIBE_OFF_COMPARE_BY_JARS[jars] ?? SUBSCRIBE_OFF_COMPARE_BY_JARS[1];
    };

    /** Subscribe total: round(MSRP × (100 − off%) / 100). */
    const subscribePriceCents = (compareCents, baseCents, jarCount) => {
      const msrp = msrpCents(compareCents, baseCents);
      const off = subscribeOffPercentForJars(jarCount);
      return Math.round((msrp * (100 - off)) / 100);
    };

    /**
     * Variant % off compare-at (one-time price), same idea as Liquid:
     * round((compare − price) / compare × 100).
     */
    const variantPercentOffCompareAt = (compareCents, baseCents) => {
      const c = Number(compareCents || 0);
      const b = Number(baseCents || 0);
      if (!(c > 0) || !(b < c)) return 0;
      return Math.round(((c - b) / c) * 100);
    };

    /** Per-jar amount from bundle line total (Shopify cents), rounded to nearest cent. */
    const perJarCentsFromBundleTotal = (totalCents, jarCount) => {
      const jc = Math.max(1, Number(jarCount) || 1);
      return Math.round(Number(totalCents || 0) / jc);
    };

    const syncBundleOptionPerJarPrices = (subscribed) => {
      for (const option of options) {
        const vid = option.getAttribute('data-variant-id');
        const v = variantsById.get(String(vid));
        if (!v) continue;

        const jars = Math.max(1, Number(v.jar_count) || 1);
        const base = Number(v.price || 0);
        const compare = Number(v.compare_at_price || 0);
        const msrp = msrpCents(compare, base);
        const row = option.querySelector('.hair-mask-lp-bundle__price');
        if (!row) continue;

        const compareWrap = row.querySelector('.hair-mask-lp-bundle__compare-at');
        const compareMoney = row.querySelector('[data-hair-mask-lp-compare-per-jar]');
        const perJarMoney = row.querySelector('[data-hair-mask-lp-per-jar]');

        const bundleTotal = subscribed ? subscribePriceCents(compare, base, jars) : base;
        const showCompare = subscribed ? msrp > bundleTotal : compare > base;

        if (compareWrap && compareMoney) {
          if (showCompare) {
            compareWrap.classList.remove('hair-mask-lp-hidden');
            compareMoney.textContent = formatMoney(perJarCentsFromBundleTotal(msrp, jars));
          } else {
            compareWrap.classList.add('hair-mask-lp-hidden');
          }
        }

        if (perJarMoney) {
          // Per-jar from MSRP×rate/jars (not total/jars) so 2-mask shows $25.97 with $51.95 total.
          const perJar = subscribed
            ? Math.round((msrp * (100 - subscribeOffPercentForJars(jars))) / 100 / jars)
            : perJarCentsFromBundleTotal(base, jars);
          perJarMoney.textContent = formatMoney(perJar);
        }

        const discountBadge = option.querySelector('.hair-mask-lp-bundle__discount-badge');
        if (discountBadge) {
          const variantPct = variantPercentOffCompareAt(compare, base);
          if (subscribed) {
            discountBadge.textContent = `${subscribeOffPercentForJars(jars)}% off`;
          } else {
            discountBadge.textContent = variantPct > 0 ? `${variantPct}% off` : 'no-discount';
          }
        }
      }
    };

    const updateUiForState = () => {
      const selectedVariant = getActiveVariant();
      if (!selectedVariant) return;

      const isSubscribed = subToggle
        ? subToggle.classList.contains('bg-current') || subToggle.classList.contains('is-on')
        : true;
      form.classList.toggle('hair-mask-lp-subscription-off', !isSubscribed);
      const selectedPlan = isSubscribed ? sellingPlanForSubscribe(selectedVariant) : null;

      variantInput.value = selectedVariant.id;
      sellingPlanInput.value = selectedPlan ? String(selectedPlan.id) : '';

      const supplyBenefitLine = document.getElementById('vanilla-sub-benefit-supply-line');
      for (const option of options) {
        const line = option.querySelector('[data-bundle-supply-line]');
        if (!line) continue;
        const supplyText = option.getAttribute('data-bundle-supply-subscribed') || '';
        line.textContent = isSubscribed ? supplyText : '';
        if (supplyBenefitLine && option.classList.contains('hair-mask-lp-bundle__option--selected')) {
          supplyBenefitLine.textContent = line.textContent.trim();
        }
      }

      if (alpineData?.pdp) {
        alpineData.pdp.selected_variant = {
          ...(alpineData.pdp.selected_variant || {}),
          id: Number(selectedVariant.id),
        };
        alpineData.pdp.selected_selling_plan = selectedPlan
          ? { id: Number(selectedPlan.id), name: selectedPlan.name }
          : null;
      }

      const comparePrice = Number(selectedVariant.compare_at_price || 0);
      const basePrice = Number(selectedVariant.price || 0);
      const jarCount = Math.max(1, Number(selectedVariant.jar_count) || 1);
      const msrp = msrpCents(comparePrice, basePrice);
      const subOffPct = subscribeOffPercentForJars(jarCount);
      const subPriceCents = subscribePriceCents(comparePrice, basePrice, jarCount);
      const variantPctOff = variantPercentOffCompareAt(comparePrice, basePrice);

      const displayPrice = isSubscribed ? subPriceCents : basePrice;

      atcButton.textContent = `Add To Cart · ${formatMoney(displayPrice)}`;

      const saveBadgeEl = document.getElementById('vanilla-sub-save-badge');
      if (saveBadgeEl && isSubscribed) {
        saveBadgeEl.textContent = `${subOffPct}% off`;
      }

      const strikeEl = document.getElementById('vanilla-sub-price-strike');
      const currentEl = document.getElementById('vanilla-sub-price-current');
      if (strikeEl && currentEl) {
        currentEl.style.removeProperty('display');
        if (isSubscribed) {
          strikeEl.textContent = formatMoney(msrp);
          strikeEl.classList.add('line-through', 'opacity-50');
          strikeEl.style.display = msrp > subPriceCents ? '' : 'none';
          currentEl.textContent = formatMoney(subPriceCents);
        } else {
          if (comparePrice > basePrice) {
            strikeEl.textContent = formatMoney(comparePrice);
            strikeEl.classList.add('line-through', 'opacity-50');
            strikeEl.style.display = '';
          } else {
            strikeEl.style.display = 'none';
          }
          currentEl.textContent = formatMoney(basePrice);
        }
      }

      const saveLineOn = document.getElementById('vanilla-sub-benefit-save-line-on');
      const saveLineOff = document.getElementById('vanilla-sub-benefit-save-line-off');
      if (saveLineOn) saveLineOn.textContent = `${subOffPct}% off today`;
      if (saveLineOff) {
        if (variantPctOff > 0) {
          saveLineOff.textContent = `${variantPctOff}% off today`;
          saveLineOff.classList.remove('hair-mask-lp-subscribe__discount-legend');
        } else {
          saveLineOff.textContent = 'NO-DISCOUNT';
          saveLineOff.classList.add('hair-mask-lp-subscribe__discount-legend');
        }
      }

      const bundleThumb =
        selectedVariant.bundle_image || selectedVariant.image;
      if (barProductImage && bundleThumb) {
        barProductImage.src = bundleThumb;
        barProductImage.srcset = `${bundleThumb} 180w, ${bundleThumb} 96w`;
      }

      if (barProductTitle) {
        barProductTitle.textContent =
          form?.getAttribute('data-product-title') || 'Glimmrlove';
      }

      if (barProductOption) {
        const bundleLabel = selectedVariant.bundle_label || selectedVariant.title || '';
        const perJar = isSubscribed
          ? Math.round((msrp * (100 - subOffPct)) / 100 / jarCount)
          : perJarCentsFromBundleTotal(basePrice, jarCount);
        barProductOption.textContent = `${bundleLabel} | ${formatMoney(perJar)}/ea`;
      }

      if (barProductPlan) {
        const supplyLabel = selectedVariant.supply_label || '';
        barProductPlan.textContent =
          isSubscribed && supplyLabel ? `Subscribe · ${supplyLabel}` : 'One-time purchase';
      }

      if (barProductCompareAt) {
        const strikeRef = isSubscribed ? msrp : comparePrice;
        const saleRef = isSubscribed ? subPriceCents : basePrice;
        if (strikeRef > saleRef) {
          barProductCompareAt.textContent = formatMoney(strikeRef);
          barProductCompareAt.classList.remove('hair-mask-lp-hidden');
        } else {
          barProductCompareAt.classList.add('hair-mask-lp-hidden');
        }
      }

      if (barProductPrice) {
        // Intermediate one-time strike only when subscribed used the old 25%-off model; hide it now.
        barProductPrice.classList.add('hair-mask-lp-hidden');
      }

      if (barProductDiscounted) {
        barProductDiscounted.textContent = formatMoney(
          isSubscribed ? subPriceCents : basePrice,
        );
      }

      const barAtcLabel = (narrowBtn) => {
        const priceStr = formatMoney(displayPrice);
        if (!selectedVariant.available) return 'OUT OF STOCK';
        return narrowBtn
          ? `ADD TO CART ${priceStr}`
          : `ADD TO CART — ${priceStr}`;
      };

      const barPickerLabel = () =>
        selectedVariant.available ? 'CHOOSE OPTIONS' : 'OUT OF STOCK';

      const narrowBar = window.matchMedia('(max-width: 1023px)').matches;

      if (barAtcButton instanceof HTMLButtonElement) {
        barAtcButton.disabled = !selectedVariant.available;
        barAtcButton.textContent = barAtcLabel(narrowBar);
      }

      if (barPickerButton instanceof HTMLButtonElement) {
        barPickerButton.disabled = !selectedVariant.available;
        barPickerButton.textContent = barPickerLabel();
      }

      syncBundleOptionPerJarPrices(isSubscribed);
    };

    for (const option of options) {
      option.addEventListener('click', () => {
        for (const otherOption of options) {
          otherOption.classList.remove('hair-mask-lp-bundle__option--selected');
        }

        option.classList.add('hair-mask-lp-bundle__option--selected');

        const radio = option.querySelector('input[type="radio"]');
        if (radio instanceof HTMLInputElement) {
          radio.checked = true;
        }

        updateUiForState();
      });
    }

    if (subToggle) {
      const toggleDot = document.getElementById('vanilla-sub-toggle-dot');
      const toggleCheck = document.getElementById('vanilla-sub-toggle-check');
      const saveBadge = document.getElementById('vanilla-sub-save-badge');
      const benefitsOn = document.getElementById('vanilla-sub-benefits-on');
      const benefitsOff = document.getElementById('vanilla-sub-benefits-off');

      subToggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const subscribed =
          subToggle.classList.contains('bg-current') || subToggle.classList.contains('is-on');

        if (subscribed) {
          subToggle.classList.remove('bg-current', 'is-on');
          subToggle.classList.add('bg-gray-300', 'is-off');
          toggleDot?.classList.remove('translate-x-6', 'hair-mask-lp-subscribe__toggle-knob--end');
          if (toggleCheck) toggleCheck.style.display = 'none';
          if (saveBadge) saveBadge.style.display = 'none';
          if (benefitsOn) benefitsOn.style.display = 'none';
          if (benefitsOff) benefitsOff.style.display = '';
        } else {
          subToggle.classList.add('bg-current', 'is-on');
          subToggle.classList.remove('bg-gray-300', 'is-off');
          toggleDot?.classList.add('translate-x-6', 'hair-mask-lp-subscribe__toggle-knob--end');
          if (toggleCheck) toggleCheck.style.display = 'block';
          if (saveBadge) saveBadge.style.display = 'block';
          if (benefitsOn) benefitsOn.style.display = '';
          if (benefitsOff) benefitsOff.style.display = 'none';
        }

        updateUiForState();
      });
    }

    updateUiForState();
  }

  const barFormEl = document.getElementById('hair-mask-lp-product-form');
  const barAtcEl = document.getElementById('bar-atc-button');
  if (barFormEl instanceof HTMLFormElement && barAtcEl instanceof HTMLButtonElement) {
    barAtcEl.addEventListener('click', () => {
      barFormEl.requestSubmit();
    });
  }

  const barPickerEl = document.getElementById('bar-scroll-to-picker-button');
  if (barPickerEl instanceof HTMLButtonElement) {
    barPickerEl.addEventListener('click', () => {
      const target = document.getElementById('hair-mask-lp-variant-picker');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        try {
          history.replaceState(null, '', '#hair-mask-lp-variant-picker');
        } catch {
          /* ignore */
        }
      }
    });
  }

  (function initProductBarMainCtaVisibility() {
    const mainCta = document.querySelector('.hair-mask-lp-cta');
    const productBar = document.getElementById('hair-mask-lp-product-bar');
    if (!(mainCta instanceof HTMLElement) || !(productBar instanceof HTMLElement)) return;

    const mq = window.matchMedia('(max-width: 1023px)');
    const hiddenClass = 'hair-mask-lp-product-bar--hidden-by-main-cta';

    const io = new IntersectionObserver(
      (entries) => {
        if (!mq.matches) {
          productBar.classList.remove(hiddenClass);
          return;
        }
        const hit = entries.some((e) => e.isIntersecting);
        productBar.classList.toggle(hiddenClass, hit);
      },
      {
        root: null,
        threshold: 0,
        rootMargin: '0px 0px 0px 0px',
      },
    );

    io.observe(mainCta);

    const onMq = () => {
      if (!mq.matches) productBar.classList.remove(hiddenClass);
    };
    mq.addEventListener('change', onMq);
  })();

  document.querySelectorAll('#hair-mask-lp-product-bar a[href^="#"]').forEach((link) => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');
      if (!href || href.length < 2) return;
      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        try {
          history.replaceState(null, '', href);
        } catch {
          /* ignore */
        }
      }
    });
  });

  (function initMarquee() {
    const track = document.getElementById('vanilla-marquee-track');
    if (!track) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const origItems = Array.from(track.children);
    if (origItems.length === 0) return;

    const dupeCount = Math.max(
      6,
      Math.ceil((window.innerWidth * 3) / (origItems.length * 300)),
    );
    for (let i = 0; i < dupeCount; i += 1) {
      for (const item of origItems) {
        track.appendChild(item.cloneNode(true));
      }
    }

    const setWidth = origItems.reduce((sum, el) => {
      const style = getComputedStyle(el);
      return (
        sum +
        el.offsetWidth +
        parseFloat(style.marginLeft || 0) +
        parseFloat(style.marginRight || 0)
      );
    }, 0);
    const gap = parseFloat(
      getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0,
    );
    const totalSetWidth = setWidth + origItems.length * gap;

    let pos = 0;
    const speed = 0.5;
    let running = true;

    const step = () => {
      pos -= speed;
      if (Math.abs(pos) >= totalSetWidth) {
        pos += totalSetWidth;
      }
      track.style.transform = `translate3d(${pos}px, 0, 0)`;
      if (running) {
        requestAnimationFrame(step);
      }
    };

    const container = document.getElementById('vanilla-marquee');
    if (!container) return;

    container.addEventListener('mouseenter', () => {
      running = false;
    });
    container.addEventListener('mouseleave', () => {
      if (!running) {
        running = true;
        requestAnimationFrame(step);
      }
    });

    requestAnimationFrame(step);
  })();

  const howToVideo = document.getElementById('hair-mask-lp-how-to-video');
  const howToPauseBtn = document.getElementById('hair-mask-lp-how-to-pause-btn');
  const howToPauseIcon = document.querySelector('.hair-mask-lp-how-to__pause-icon--pause');
  const howToPlayIcon = document.querySelector('.hair-mask-lp-how-to__pause-icon--play');
  if (howToVideo instanceof HTMLVideoElement && howToPauseBtn instanceof HTMLButtonElement) {
    const syncHowToPlaybackUi = () => {
      const playing = !howToVideo.paused;
      if (playing) {
        howToPauseIcon?.classList.remove('hair-mask-lp-hidden');
        howToPlayIcon?.classList.add('hair-mask-lp-hidden');
        howToPauseBtn.setAttribute('aria-label', 'Pause video');
      } else {
        howToPauseIcon?.classList.add('hair-mask-lp-hidden');
        howToPlayIcon?.classList.remove('hair-mask-lp-hidden');
        howToPauseBtn.setAttribute('aria-label', 'Play video');
      }
    };

    howToVideo.addEventListener('play', syncHowToPlaybackUi);
    howToVideo.addEventListener('pause', syncHowToPlaybackUi);

    howToPauseBtn.addEventListener('click', () => {
      if (howToVideo.paused) {
        void howToVideo.play();
      } else {
        howToVideo.pause();
      }
      syncHowToPlaybackUi();
    });

    void howToVideo.play().then(syncHowToPlaybackUi).catch(syncHowToPlaybackUi);
  }

  const upsellAtcButton = document.getElementById('upsell-clean-shine-revive-trio-atc');
  if (upsellAtcButton instanceof HTMLButtonElement) {
    upsellAtcButton.addEventListener('click', async (event) => {
      event.preventDefault();
      event.stopPropagation();

      const variantId = upsellAtcButton.getAttribute('data-variant-id');
      if (!variantId) return;

      const originalText = upsellAtcButton.textContent;
      upsellAtcButton.disabled = true;
      upsellAtcButton.textContent = 'Adding...';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: Number(variantId),
            quantity: 1,
          }),
        });

        if (!response.ok) throw new Error('Add to cart failed');

        window.location.href = '/cart';
      } catch {
        upsellAtcButton.disabled = false;
        upsellAtcButton.textContent = originalText || 'Add to Cart';
      }
    });
  }
});
